import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  BrazilianRegionalHolidayPreviewItem,
  BrazilianRegionalUfOption,
  importBrazilianRegionalHolidays,
  listBrazilianRegionalHolidayCatalog,
  previewBrazilianRegionalHolidays,
} from "../../../services/calendarExceptionService";
import { Unit } from "../../../services/unitService";

export type BrazilianRegionalHolidaysImportSummary = {
  created: number;
  skipped: number;
  year: number;
  uf: string;
};

interface ImportBrazilianRegionalHolidaysModalProps {
  isOpen: boolean;
  units: Unit[];
  submitting: boolean;
  initialYear?: number;
  /** When true, render panel content without outer Modal chrome (wizard tabs). */
  embedded?: boolean;
  onClose: () => void;
  onSubmittingChange: (value: boolean) => void;
  onImported: (summary: BrazilianRegionalHolidaysImportSummary) => void;
}

const currentYear = (): number => new Date().getFullYear();

const resolvePreviewStatus = (
  holiday: BrazilianRegionalHolidayPreviewItem
): "new" | "already" | "skip" => {
  if (holiday.has_manual_overlap) {
    return "skip";
  }

  if (holiday.already_imported) {
    return "already";
  }

  return "new";
};

const ImportBrazilianRegionalHolidaysModal = ({
  isOpen,
  units,
  submitting,
  initialYear,
  embedded = false,
  onClose,
  onSubmittingChange,
  onImported,
}: ImportBrazilianRegionalHolidaysModalProps) => {
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState<"configure" | "preview">("configure");
  const [year, setYear] = useState(String(initialYear ?? currentYear()));
  const [uf, setUf] = useState("");
  const [unitId, setUnitId] = useState("");
  const [includeOptionalDays, setIncludeOptionalDays] = useState(false);
  const [includeCity, setIncludeCity] = useState(true);
  const [ufs, setUfs] = useState<BrazilianRegionalUfOption[]>([]);
  const [holidays, setHolidays] = useState<BrazilianRegionalHolidayPreviewItem[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [capitalHint, setCapitalHint] = useState<string | null>(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStep("configure");
    setYear(String(initialYear ?? currentYear()));
    setUnitId("");
    setIncludeOptionalDays(false);
    setIncludeCity(true);
    setHolidays([]);
    setSelectedCodes([]);
    setCapitalHint(null);
    setError(null);

    const unitWithState = units.find((unit) => typeof unit.state === "string" && unit.state.length === 2);
    setUf(unitWithState?.state ? unitWithState.state.toUpperCase() : "");

    let cancelled = false;
    setLoadingCatalog(true);

    void listBrazilianRegionalHolidayCatalog()
      .then((response) => {
        if (!cancelled) {
          setUfs(response.data.ufs);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("studio.settings.calendar_exceptions.brRegional.toast.catalogError"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCatalog(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialYear, isOpen, t, units]);

  useEffect(() => {
    if (!unitId) {
      return;
    }

    const unit = units.find((item) => String(item.id) === unitId);
    if (unit?.state && unit.state.length === 2) {
      setUf(unit.state.toUpperCase());
    }
  }, [unitId, units]);

  const holidayName = (item: BrazilianRegionalHolidayPreviewItem): string => {
    const key = `studio.settings.calendar_exceptions.brRegional.names.${item.code}`;
    const translated = t(key);

    return translated === key ? item.name : translated;
  };

  const formatDate = (date: string): string => {
    const [y, m, d] = date.split("-").map(Number);
    if (!y || !m || !d) {
      return date;
    }

    return new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
      new Date(y, m - 1, d)
    );
  };

  const selectedCount = selectedCodes.length;

  const allSelected = useMemo(
    () => holidays.length > 0 && selectedCodes.length === holidays.length,
    [holidays.length, selectedCodes.length]
  );

  const statusBadge = (status: "new" | "already" | "skip") => {
    if (status === "skip") {
      return (
        <Badge color="warning" className="ms-1">
          {t("studio.settings.calendar_exceptions.brHolidays.preview.status.skip")}
        </Badge>
      );
    }

    if (status === "already") {
      return (
        <Badge color="info" className="ms-1">
          {t("studio.settings.calendar_exceptions.brHolidays.preview.status.already")}
        </Badge>
      );
    }

    return (
      <Badge color="success" className="ms-1">
        {t("studio.settings.calendar_exceptions.brHolidays.preview.status.new")}
      </Badge>
    );
  };

  const loadPreview = async () => {
    const parsedYear = Number(year);

    if (!Number.isInteger(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      setError(t("studio.settings.calendar_exceptions.brHolidays.validation.yearInvalid"));
      return;
    }

    if (!uf) {
      setError(t("studio.settings.calendar_exceptions.brRegional.validation.ufRequired"));
      return;
    }

    setLoadingPreview(true);
    setError(null);

    try {
      const response = await previewBrazilianRegionalHolidays({
        year: parsedYear,
        uf,
        unit_id: unitId ? Number(unitId) : undefined,
        include_optional_days: includeOptionalDays,
        include_city: includeCity,
      });

      const items = response.data.holidays;
      setHolidays(items);
      setSelectedCodes(
        items.filter((item) => !item.has_manual_overlap).map((item) => item.code)
      );

      const match = response.data.capital_match;
      if (includeCity && match) {
        setCapitalHint(
          match.matched
            ? t("studio.settings.calendar_exceptions.brRegional.capital.matched", {
                city: match.city,
              })
            : t("studio.settings.calendar_exceptions.brRegional.capital.notMatched", {
                city: match.city,
              })
        );
      } else {
        setCapitalHint(null);
      }

      setStep("preview");
    } catch (err: any) {
      setError(err?.data || t("studio.settings.calendar_exceptions.brRegional.toast.previewError"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleCode = (code: string) => {
    setSelectedCodes((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedCodes([]);
      return;
    }

    setSelectedCodes(holidays.map((item) => item.code));
  };

  const confirmImport = async () => {
    if (selectedCodes.length === 0) {
      setError(t("studio.settings.calendar_exceptions.brHolidays.validation.selectAtLeastOne"));
      return;
    }

    onSubmittingChange(true);
    setError(null);

    try {
      const response = await importBrazilianRegionalHolidays({
        year: Number(year),
        uf,
        unit_id: unitId ? Number(unitId) : null,
        include_optional_days: includeOptionalDays,
        include_city: includeCity,
        codes: selectedCodes,
      });

      onImported({
        created: response.data.created,
        skipped: response.data.skipped.length,
        year: Number(year),
        uf,
      });
      onClose();
    } catch (err: any) {
      setError(err?.data || t("studio.settings.calendar_exceptions.brRegional.toast.importError"));
    } finally {
      onSubmittingChange(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const body = (
    <>
        {error ? (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {step === "configure" ? (
          <>
            <p className="text-muted mb-3">
              {t("studio.settings.calendar_exceptions.brRegional.modal.description")}
            </p>
            {loadingCatalog ? (
              <div className="text-center py-3">
                <Spinner color="primary" size="sm" />
              </div>
            ) : null}
            <FormGroup>
              <Label for="br-regional-year">
                {t("studio.settings.calendar_exceptions.brHolidays.fields.year")}
              </Label>
              <Input
                id="br-regional-year"
                type="number"
                min={2000}
                max={2100}
                value={year}
                onChange={(event) => setYear(event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="br-regional-uf">
                {t("studio.settings.calendar_exceptions.brRegional.fields.uf")}
              </Label>
              <Input
                id="br-regional-uf"
                type="select"
                value={uf}
                onChange={(event) => setUf(event.target.value)}
              >
                <option value="">
                  {t("studio.settings.calendar_exceptions.brRegional.fields.ufPlaceholder")}
                </option>
                {ufs.map((option) => (
                  <option key={option.uf} value={option.uf}>
                    {option.uf} — {option.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="br-regional-unit">
                {t("studio.settings.calendar_exceptions.fields.unit")}
              </Label>
              <Input
                id="br-regional-unit"
                type="select"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                <option value="">
                  {t("studio.settings.calendar_exceptions.fields.allUnits")}
                </option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                    {unit.state ? ` (${unit.state})` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup check className="mb-2">
              <Input
                id="br-regional-optional"
                type="checkbox"
                checked={includeOptionalDays}
                onChange={(event) => setIncludeOptionalDays(event.target.checked)}
              />
              <Label check for="br-regional-optional">
                {t("studio.settings.calendar_exceptions.brRegional.fields.includeOptional")}
              </Label>
              <div className="form-text">
                {t("studio.settings.calendar_exceptions.brRegional.fields.includeOptionalHelp")}
              </div>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input
                id="br-regional-city"
                type="checkbox"
                checked={includeCity}
                onChange={(event) => setIncludeCity(event.target.checked)}
              />
              <Label check for="br-regional-city">
                {t("studio.settings.calendar_exceptions.brRegional.fields.includeCity")}
              </Label>
              <div className="form-text">
                {t("studio.settings.calendar_exceptions.brRegional.fields.includeCityHelp")}
              </div>
            </FormGroup>
          </>
        ) : (
          <>
            <p className="text-muted mb-2">
              {t("studio.settings.calendar_exceptions.brRegional.preview.hint", {
                year,
                uf,
              })}
            </p>
            {capitalHint ? (
              <Alert color="info" className="py-2">
                {capitalHint}
              </Alert>
            ) : null}
            {holidays.length === 0 ? (
              <Alert color="warning">
                {t("studio.settings.calendar_exceptions.brRegional.preview.empty")}
              </Alert>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Button color="link" className="p-0" onClick={toggleAll}>
                    {allSelected
                      ? t("studio.settings.calendar_exceptions.brHolidays.preview.unselectAll")
                      : t("studio.settings.calendar_exceptions.brHolidays.preview.selectAll")}
                  </Button>
                  <span className="text-muted small">
                    {t("studio.settings.calendar_exceptions.brHolidays.preview.selectedCount", {
                      count: selectedCount,
                    })}
                  </span>
                </div>
                <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: "auto" }}>
                  {holidays.map((holiday) => {
                    const status = resolvePreviewStatus(holiday);

                    return (
                      <FormGroup
                        check
                        key={holiday.code}
                        className={`mb-2 p-2 rounded ${
                          status === "skip"
                            ? "bg-warning-subtle"
                            : status === "already"
                              ? "bg-info-subtle"
                              : "bg-success-subtle"
                        }`}
                      >
                        <Input
                          id={`br-regional-${holiday.code}`}
                          type="checkbox"
                          checked={selectedCodes.includes(holiday.code)}
                          onChange={() => toggleCode(holiday.code)}
                        />
                        <Label check for={`br-regional-${holiday.code}`} className="w-100">
                          <div className="d-flex flex-wrap justify-content-between gap-2">
                            <span>
                              {holidayName(holiday)}
                              {statusBadge(status)}
                              <Badge color="secondary" className="ms-1">
                                {holiday.scope === "city"
                                  ? t("studio.settings.calendar_exceptions.brRegional.scope.city")
                                  : t("studio.settings.calendar_exceptions.brRegional.scope.state")}
                              </Badge>
                              {holiday.optional ? (
                                <span className="text-muted ms-1">
                                  (
                                  {t(
                                    "studio.settings.calendar_exceptions.brHolidays.preview.optional"
                                  )}
                                  )
                                </span>
                              ) : null}
                            </span>
                            <span className="text-muted">{formatDate(holiday.date)}</span>
                          </div>
                        </Label>
                      </FormGroup>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
    </>
  );

  const footer = (
    <div className="d-flex flex-wrap justify-content-end gap-2">
      {step === "preview" ? (
        <Button color="light" disabled={submitting} onClick={() => setStep("configure")}>
          {t("studio.settings.calendar_exceptions.brHolidays.actions.back")}
        </Button>
      ) : (
        <Button color="light" onClick={onClose}>
          {t("studio.settings.calendar_exceptions.actions.cancel")}
        </Button>
      )}
      {step === "configure" ? (
        <Button color="primary" disabled={loadingPreview || loadingCatalog} onClick={loadPreview}>
          {loadingPreview ? <Spinner size="sm" className="me-1" /> : null}
          {t("studio.settings.calendar_exceptions.brHolidays.actions.preview")}
        </Button>
      ) : (
        <Button
          color="primary"
          disabled={submitting || selectedCount === 0}
          onClick={confirmImport}
        >
          {submitting ? <Spinner size="sm" className="me-1" /> : null}
          {t("studio.settings.calendar_exceptions.brHolidays.actions.confirm")}
        </Button>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div>
        {body}
        <div className="mt-3">{footer}</div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("studio.settings.calendar_exceptions.brRegional.modal.title")}
      </ModalHeader>
      <ModalBody>{body}</ModalBody>
      <ModalFooter>{footer}</ModalFooter>
    </Modal>
  );
};

export default ImportBrazilianRegionalHolidaysModal;
